// applyEnvironment — single entry point for "use this env_asset as the
// active gsplat background." Both the content-browser context menu and
// the EnvironmentInspector call this so behavior stays consistent.
//
// Does three things in order:
//   1. Sets liveTuneStore.environment.splatUrl + applies the asset's
//      saved default_pose. LiveTunePanel's dropdown reads from the same
//      store, so it reflects the change automatically.
//   2. Switches the app into Live View if it isn't already (so the
//      splat is actually visible — edit mode renders a different scene).
//   3. Returns the URL it applied, so callers can surface a confirmation.
//
// `source` says who asked, and only 'user' persists:
//   'user'      (default) — the content browser, the inspector, a viewport drop.
//                 A chosen environment survives a reload, as it always has.
//   'blueprint' — a loadEnvironment node. Stripped from the stored tune, and
//                 unloaded again when no such node is left (see below).
//   'url'       — a `?environment=` deep link. Also stripped, because a link
//                 someone followed once must not silently rewrite their stored
//                 environment for every later visit; but NOT subject to node
//                 teardown, since a link needs no node to justify it.
//
// Dangling-safe: If orgBucket is provided and the asset is no longer in it,
// returns null without applying or throwing. This handles the case where an
// org environment was deleted after being set as active.
//
// Explicit .js extensions below: this module is imported by node:test
// directly (tests/unit/applyEnvironment.test.mjs), and Node ESM requires the
// extension. Everything it imports is already extension-safe itself.

import { liveTuneStore } from './liveTuneStore.js'
import { normalizePose } from './envPose.js'
import { extractTuneFromAsset, applyTuneForEnvironment } from './envTune.js'
import { noteAssetIdForSplatUrl } from './environmentFiles.js'
import { environmentAssetId } from './environmentAssetId.js'

export { normalizePose }

// Predicate: is this environment reference (id) no longer in the org bucket?
// Pure function, testable without the whole app.
// Returns true if:
//   - asset is null/undefined
//   - asset.id doesn't exist
//   - asset.id is not found in orgBucket
export function isDanglingEnvironmentReference(asset, orgBucket) {
  if (!asset) return true
  // Compare SERVER ids on both sides. A content-browser asset carries the
  // registry's namespaced `environment:<uuid>` while the bucket holds raw rows,
  // so comparing `id` to `id` reports every registry asset as dangling and
  // refuses to apply a perfectly live environment.
  const id = environmentAssetId(asset)
  if (!id) return true
  return !orgBucket.some(e => environmentAssetId(e) === id)
}

export function applyEnvironment(asset, orgBucket = null, { source = 'user', urlOverride = null } = {}) {
  if (!asset) return null

  // Check for dangling reference: if orgBucket is provided and the asset
  // is no longer in it, return null and don't apply.
  if (orgBucket !== null && orgBucket !== undefined && isDanglingEnvironmentReference(asset, orgBucket)) {
    console.warn('[applyEnvironment] dangling environment reference (no longer in org bucket):', asset.id)
    return null
  }

  // Asset can come from assetRegistry (kind:'environment') OR from the
  // CustomAssetManager raw row. Resolve the URL + pose from either shape.
  //
  // `urlOverride` swaps ONLY the splat bytes — a format-keyed variant the server
  // published (ENG-1070) — while pose and shared tune still come from the row:
  // they describe the environment, not one encoding of it. Read verbatim off the
  // files map by the caller; nothing here derives a path.
  const url = urlOverride ?? asset.url ?? asset.fileUrl ?? asset.file_url ?? asset.raw?.fileUrl ?? asset.raw?.file_url
  if (!url) {
    console.warn('[applyEnvironment] no URL on asset; nothing to apply', asset)
    return null
  }
  const rawPose = asset.defaultPose ?? asset.default_pose ?? asset.raw?.default_pose ?? asset.raw?.defaultPose ?? {}
  const pose = normalizePose(rawPose)

  // Tell environmentFiles which environment this URL belongs to, BEFORE the
  // store write below — a subscriber reacting to that write (the artifact
  // volumes) asks immediately, and must not cache a miss first.
  //
  // Load-bearing for a format-pinned load: `url` is then a file from the
  // environment's `files` map, and environmentFiles' URL match runs against the
  // environment LIST, whose serve URLs are shaped differently for the very same
  // object. Nothing there equals it, so without this the id resolved null and the
  // whole baked set (lightprobes, reflections, navmesh, collision, voxels) of a
  // fully baked environment read as absent — and a re-bake could not fix it, because
  // bakeStatusStore invalidates the stale files map BY ASSET ID and a URL cached
  // with a null id is invisible to that scan.
  const assetId = environmentAssetId(asset)
  if (assetId) noteAssetIdForSplatUrl(url, assetId)

  // Same live effect for every source; what differs is whether the value is
  // allowed into localStorage. Without this the first shipped node left
  // Basement_Level booting on a character whose graph no longer mentioned it,
  // and a shared `?environment=` link rewrote the recipient's stored
  // environment for good. See tuneAuthored.js.
  const patch = { environment: { splatUrl: url, pose } }
  if (source === 'user') liveTuneStore.setState(patch)
  else liveTuneStore.setStateAuthored(patch, { source })

  // assetId: state it directly from the row we already resolved it from —
  // never re-derive it from the URL. Without this, a format-pinned load
  // (ENG-1070's `urlOverride`, e.g. a PLY variant) sets splatUrl to a file
  // that isn't the row's own `.url`, and environmentFiles.js's async
  // URL-matching fallback (which exists for the cases with no row in hand,
  // like a `?splat=` deep link) can never match it either — no row in the
  // org list carries per-format URLs to match against (deliberately: the
  // list omits `files` because it can't tell which artifacts exist). The
  // panel's ENVIRONMENT dropdown (currentEnv) and everything gated on it
  // (Save for everyone, the pose save) match by this id first.
  //
  // Always EPHEMERAL regardless of `source` — assetId is server-derived, not
  // a setting the user chose (see the DEFAULTS.environment.assetId comment
  // in liveTuneStore.js): folding it into the patch above would persist it
  // for a 'user' pick, which is exactly what setStateEphemeral exists to avoid.
  if (assetId) liveTuneStore.setStateEphemeral({ environment: { assetId } })

  // Shared lighting: apply the environment's saved tune (default_tune) so
  // every viewer gets the authored look, not their own localStorage drift.
  // No saved tune → local knobs stay untouched (see envTune.js semantics).
  applyTuneForEnvironment(extractTuneFromAsset(asset), { source })

  // No mode flip needed any more (Wave 2B "one mode"): the viewport is
  // always live, so writing splatUrl to liveTuneStore is what mounts the
  // environment — Scene's env stack reads it directly.

  return url
}

/**
 * Unload a BLUEPRINT-authored environment — the teardown half of the
 * `loadEnvironment` node (ENG-1031).
 *
 * Deleting a node has to undo what the node did, or the editor lies: Andres
 * deleted his Load Environment node and the splat stayed on screen (outliner
 * row and all), because nothing had ever unloaded an environment before — the
 * only way in was a human picking one, and a pick has no node to delete.
 *
 * Refuses in two cases, both deliberate:
 *   - nothing loaded: no work, no log.
 *   - the active environment did not come from the blueprint — a human pick
 *     (content browser, inspector, viewport drop) or a `?environment=` deep
 *     link. Editing the graph must not throw away a choice the graph never
 *     made, and a link needs no node to justify it — open one on a character
 *     whose blueprint has no loadEnvironment node and the teardown would
 *     otherwise unload it a frame later. `liveTuneStore.authoredSource` is the
 *     difference.
 *
 * Leaves the shared look (lighting/shadow/backdrop slices the environment's
 * `default_tune` applied) where it is: those are live knobs an artist may have
 * kept working in, and snapping them back to defaults on a graph edit would
 * destroy in-progress tuning to undo a splat.
 *
 * @returns {boolean} whether it unloaded anything.
 */
export function unloadAuthoredEnvironment() {
  const env = liveTuneStore.getState()?.environment
  if (!env?.splatUrl) return false
  if (liveTuneStore.authoredSource('environment.splatUrl') !== 'blueprint') return false
  liveTuneStore.setStateAuthored({ environment: { splatUrl: null, pose: normalizePose({}) } })
  // assetId is ephemeral and set outside this patch (see applyEnvironment) —
  // clear it the same way, so a stale id from the unloaded row can't outlive
  // its splatUrl and wrongly match a later pick during the gap before one lands.
  liveTuneStore.setStateEphemeral({ environment: { assetId: null } })
  return true
}
